import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Button } from 'components/UIElements'

const toBase64 = canvas => {
  if (!canvas) return ''
  const dataUrl = canvas.toDataURL('image/png')
  return dataUrl.split(',')[1] || ''
}

const isCanvasBlank = canvas => {
  if (!canvas) return true
  const ctx = canvas.getContext('2d')
  const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data
  for (let i = 3; i < pixels.length; i += 4) {
    if (pixels[i] !== 0) return false
  }
  return true
}

/**
 * Lightweight draw pad (ClinRO-style). Emits PNG base64 without data: prefix.
 */
const DcfSignaturePad = ({
  signature = '',
  disabled = false,
  hideClear = false,
  clearDisabled = false,
  onChange = () => {},
}) => {
  const canvasRef = useRef(null)
  const drawingRef = useRef(false)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return undefined
    const ctx = canvas.getContext('2d')
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.strokeStyle = '#111'

    if (!signature) {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      return undefined
    }

    let cancelled = false
    const image = new Image()
    image.onload = () => {
      // onload can fire after a later effect already cleared the canvas, so a
      // superseded signature must not repaint itself.
      if (cancelled) return
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
    }
    image.src = signature.includes('data:image')
      ? signature
      : `data:image/png;base64,${signature}`

    return () => {
      cancelled = true
    }
  }, [signature])

  const pointFromEvent = event => {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const source = event.touches ? event.touches[0] : event
    return {
      x: ((source.clientX - rect.left) / rect.width) * canvas.width,
      y: ((source.clientY - rect.top) / rect.height) * canvas.height,
    }
  }

  const startDraw = event => {
    if (disabled) return
    event.preventDefault()
    drawingRef.current = true
    const ctx = canvasRef.current.getContext('2d')
    const point = pointFromEvent(event)
    ctx.beginPath()
    ctx.moveTo(point.x, point.y)
  }

  const draw = event => {
    if (!drawingRef.current || disabled) return
    event.preventDefault()
    const ctx = canvasRef.current.getContext('2d')
    const point = pointFromEvent(event)
    ctx.lineTo(point.x, point.y)
    ctx.stroke()
  }

  const endDraw = () => {
    if (!drawingRef.current) return
    drawingRef.current = false
    if (!isCanvasBlank(canvasRef.current)) {
      onChange(toBase64(canvasRef.current))
    }
  }

  const clear = () => {
    if (disabled || clearDisabled) return
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    onChange('')
  }

  return (
    <div className='dcf-signature-pad'>
      <canvas
        ref={canvasRef}
        aria-label='Signature drawing area'
        width={480}
        height={140}
        className={disabled ? 'disabled' : ''}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
      {!hideClear && (
        <Button
          grey
          type='button'
          content='Clear signature'
          disabled={disabled || clearDisabled}
          onClick={clear}
        />
      )}
    </div>
  )
}

DcfSignaturePad.propTypes = {
  signature: PropTypes.string,
  disabled: PropTypes.bool,
  hideClear: PropTypes.bool,
  clearDisabled: PropTypes.bool,
  onChange: PropTypes.func,
}

export default DcfSignaturePad
